import React, { useState, useEffect, useRef } from 'react'
import { Search, Loader2, ChevronDown, X, ShieldAlert, HelpCircle } from 'lucide-react'
import { nsApi } from '../services/api'

// ─── NsRecordTypeCombobox ─────────────────────────────────────────────────────
//
// A real combobox for picking one NetSuite record type: opens on focus and
// shows results immediately (browse mode — the account's own record types,
// alphabetically), narrows as you type, and shows the chosen record as a
// clearly-labelled chip distinct from free-typed text. Used for both the
// "which record type is this document" picker and the "which record holds
// the line items" picker, so a fix here (or a UX improvement) lands in both
// at once instead of drifting between two hand-rolled copies.
//
// Previously each caller re-implemented this ad hoc, and the line-record
// picker specifically only showed a query typed into another field's own
// value, was easy to mistake for a value already chosen when combined with
// ordinary browser autocomplete on a plain <input>.
//
// Results are filtered server-side to record types confirmed to hold real
// data by default — see NsRecordTypeCombobox's "show all" toggle below and
// search_ns_record_types on the backend. A type the background probe
// couldn't reach a confirmed answer for (never probed yet, no permission, or
// an error) is still shown, badged with why, rather than silently hidden —
// "we don't know" and "it's empty" are different answers.

function ProbeBadge({ rt }) {
  if (rt.probe_status === 'forbidden') {
    return (
      <span title="This role has no permission to read this record type" className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
        <ShieldAlert className="h-3 w-3" /> No permission
      </span>
    )
  }
  if (rt.probe_status === 'never_probed' || rt.probe_status === 'throttled' || rt.probe_status === 'error') {
    return (
      <span title="Not yet confirmed whether this record type holds data" className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
        <HelpCircle className="h-3 w-3" /> Not yet checked
      </span>
    )
  }
  return null
}

export default function NsRecordTypeCombobox({
  value, onChange, placeholder = 'Search NetSuite record types…',
  excludeKey, disabled = false, autoFocus = false,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')
  const [hiddenCount, setHiddenCount] = useState(0)
  const [showAll, setShowAll] = useState(false)
  const ref = useRef(null)
  const probeTriggeredRef = useRef(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setSearching(true)
    setError('')
    const t = setTimeout(() => {
      nsApi.searchRecordTypes(query, 40, showAll)
        .then(res => {
          if (cancelled) return
          const rows = (res.data?.record_types || []).filter(r => r.record_type_key !== excludeKey)
          setResults(rows)
          setHiddenCount(res.data?.hidden_count || 0)
        })
        .catch(err => {
          if (cancelled) return
          setResults([])
          setHiddenCount(0)
          setError(err?.response?.data?.error || 'Could not load NetSuite record types.')
        })
        .finally(() => { if (!cancelled) setSearching(false) })
    }, 250)
    return () => { cancelled = true; clearTimeout(t) }
  }, [open, query, excludeKey, showAll])

  // Nudge the background "has data" probe once per time this combobox opens
  // — bounded to one batch server-side, so this is cheap even if the
  // catalog is large, and is what keeps the very first-ever open from being
  // a dead end while the periodic sweep is still converging. Deliberately
  // fire-and-forget: a slow/failed probe trigger must never block or error
  // the picker itself, which already has its own real data to show.
  useEffect(() => {
    if (!open || probeTriggeredRef.current) return
    probeTriggeredRef.current = true
    nsApi.triggerRecordTypeProbe().catch(() => { /* best-effort only */ })
  }, [open])

  useEffect(() => {
    if (!open) { probeTriggeredRef.current = false; setShowAll(false) }
  }, [open])

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function handleSelect(rt) {
    onChange(rt)
    setQuery('')
    setOpen(false)
  }

  function handleClear(e) {
    e.stopPropagation()
    onChange(null)
    setQuery('')
  }

  return (
    <div ref={ref} className="relative">
      {value ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => { onChange(null); setOpen(true) }}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-left disabled:cursor-not-allowed"
        >
          <span className="min-w-0">
            <span className="block text-[13px] font-medium text-green-800 dark:text-green-300 truncate">{value.display_label}</span>
            <span className="block text-[11px] text-green-600/80 dark:text-green-400/70 font-mono truncate">{value.record_type_key}</span>
          </span>
          {!disabled && (
            <span onClick={handleClear} className="flex-shrink-0 p-1 rounded hover:bg-green-100 dark:hover:bg-green-800/40 text-green-600 dark:text-green-400">
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </button>
      ) : (
        <div className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${open ? 'border-navy-400 ring-2 ring-navy-100 dark:ring-navy-900' : 'border-gray-300 dark:border-gray-600'}`}>
          <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            autoFocus={autoFocus}
            autoComplete="off"
            disabled={disabled}
            value={query}
            onFocus={() => setOpen(true)}
            onChange={e => { setQuery(e.target.value); setOpen(true) }}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-[13px] focus:outline-none text-gray-900 dark:text-gray-100 min-w-0"
          />
          {searching ? <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin flex-shrink-0" /> : <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />}
        </div>
      )}

      {open && !value && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl max-h-56 overflow-y-auto">
          {error ? (
            <p className="px-3 py-3 text-[12px] text-red-500">{error}</p>
          ) : results.length === 0 && !searching ? (
            <p className="px-3 py-3 text-[12px] text-gray-400 italic text-center">No matching record types</p>
          ) : (
            results.map(rt => (
              <button
                key={rt.record_type_key}
                type="button"
                onClick={() => handleSelect(rt)}
                className="w-full text-left px-3 py-2 flex items-center justify-between gap-2 hover:bg-navy-50 dark:hover:bg-navy-900/20"
              >
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium text-gray-900 dark:text-gray-100 truncate">{rt.display_label}</span>
                  {/* The internal key too, not just the label — some accounts have
                      several similarly-named custom records and the label alone
                      can't tell them apart. */}
                  <span className="block text-[11px] text-gray-400 font-mono truncate">{rt.record_type_key}</span>
                </span>
                <span className="flex-shrink-0 flex items-center gap-1">
                  <ProbeBadge rt={rt} />
                  {rt.is_custom_record && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">Custom</span>
                  )}
                </span>
              </button>
            ))
          )}
          {!showAll && hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="w-full text-center px-3 py-2 text-[12px] font-medium text-navy-600 dark:text-navy-400 hover:bg-navy-50 dark:hover:bg-navy-900/20 border-t border-gray-100 dark:border-gray-700"
            >
              Show all record types ({hiddenCount} hidden — confirmed empty)
            </button>
          )}
        </div>
      )}
    </div>
  )
}
